/**
 * ═══ SKT TARİH KURALI — İSTEMCİ AYNASI (kullanıcı kararı, 2 Eki 2026) ═══
 *
 * ESAS KURAL SUNUCUDADIR: `apps/api/src/inventory.ts` → `sktTarihHatasi()`, fiş düzenlemede ayrıca
 * `apps/api/src/routers/inventory.ts` → `updateReceiptLines` (hangi satır denetlenir, hangi bağlamla).
 * Bu dosya yalnız ANINDA GERİ BİLDİRİM içindir (alanın altında hata + kaydetmeyi engelleme); sunucu
 * her uçta aynı kuralı yeniden uygular ve kendi BAD_REQUEST mesajını döner. Kural ya da mesaj metni
 * değişirse İKİ TARAF birlikte değişmeli — ayna kuralı: aynı soruyu soran iki yer aynı cevabı
 * vermeli, yoksa form "geçerli" deyip sunucu reddeder (ya da tersi).
 *
 * Kural (takvim GÜNÜ, Europe/Istanbul):
 *  · Üretim tarihi / SKT yılı 2000–2100 dışında → yıl yazım hatası (canlıda "0026" girilmiş lot var).
 *  · SKT, girilen ÜRETİM tarihinden önce olamaz — her giriş türünde (aynı gün olabilir).
 *  · SATIN ALMA kabulünde SKT, KABUL gününden SONRA olmalı — SKT'si geçmiş ya da kabul günü dolan
 *    mal satın alınmaz (aynı gün de RED: MRP/FEFO onu o gün "SKT geçmiş" sayar — bulgu G2).
 *  · AÇILIŞ STOĞU üretim tarihi girilmemişse serbest (sayımda SKT'si geçmiş mal bulunabilir).
 *
 * İSTEMCİNİN BİLEMEDİĞİ TEK ŞEY: SKT boş bırakılınca sunucu onu kartın RAF ÖMRÜNDEN hesaplar ve
 * "Raf ömründen hesaplanan SKT …" diye denetler; `itemOptions` raf ömrünü döndürmediği için istemci
 * boş SKT'de yalnız yıl kuralını uygular. Bu yön GÜVENLİDİR (istemci sunucudan GEVŞEK kalır, asla
 * sıkı değil) ve sunucunun mesajı formun üst bandında görünür.
 *
 * Girdiler `<input type="date">` sözleşmesindeki "YYYY-MM-DD" metinleridir. Form bu metni
 * sunucuya `new Date(metin).toISOString()` (UTC gece yarısı) olarak gönderir; o anın İstanbul
 * günü metnin kendisidir (UTC+3) — bu yüzden metin karşılaştırması sunucunun
 * `istanbulGunu(...)` karşılaştırmasıyla birebir aynı sonucu verir.
 */

export type SktOrigin = "production" | "purchase" | "opening_stock";

/** Sunucudan gelen serbest metin kaynağı daralt — sunucudaki `origin ?? "purchase"` ile aynı varsayılan. */
export function sktOrigin(v: string | null | undefined): SktOrigin {
  return v === "production" || v === "opening_stock" ? v : "purchase";
}

export interface SktKuralGirdi {
  /** SKT, "YYYY-MM-DD" ya da boş. */
  expiresAt: string | null | undefined;
  /** AÇIKÇA girilen üretim tarihi, "YYYY-MM-DD" ya da boş. */
  producedAt: string | null | undefined;
  /**
   * Kabul günü, "YYYY-MM-DD" (Europe/Istanbul). `null` = BİLİNMİYOR → kabul-günü kuralı
   * UYGULANMAZ, yalnız yıl + üretim tarihi kuralı.
   */
  kabulGunu: string | null;
  /** Lotun kaynağı. Sunucudaki gibi boşsa `purchase` sayılır. */
  origin: SktOrigin | null | undefined;
}

/** Alan bazında hata — form, mesajı ilgili alanın ALTINDA gösterir. */
export interface SktAlanHatalari {
  producedAt: string | null;
  expiresAt: string | null;
}

/** Tarih alanlarına verilecek mutlak sınırlar (yıl kuralının seçici tarafı). */
export const SKT_YIL_MIN_GUN = "2000-01-01";
export const SKT_YIL_MAX_GUN = "2100-12-31";

const istanbulGunFmt = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Istanbul" }); // YYYY-MM-DD
const trTarihFmt = new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul" });

/**
 * Bir anın Europe/Istanbul takvim günü, "YYYY-MM-DD". Geçersiz girdide boş metin.
 * Yıl 4 haneye TAMAMLANIR: Intl 1000'den küçük yılı "26-02-01" diye basar — bu `type="date"` için
 * geçersizdir, alan BOŞ görünür ve canlıdaki "0026" yıllı eski lotun hatası düzenlemede gizlenirdi.
 */
export function istanbulGunu(d: Date | string): string {
  const t = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(t.getTime())) return "";
  const p = Object.fromEntries(istanbulGunFmt.formatToParts(t).map((x) => [x.type, x.value]));
  return `${String(p.year).padStart(4, "0")}-${p.month}-${p.day}`;
}

/** Bugünün Europe/Istanbul takvim günü — satın alma kabulünde "kabul günü" budur. */
export function istanbulBugun(): string {
  return istanbulGunu(new Date());
}

const GUN_RE = /^(\d{4,})-(\d{2})-(\d{2})$/;

/** "YYYY-MM-DD"nin ERTESİ günü (takvim aritmetiği UTC'de — saat dilimi/yaz saati kaydırmaz). */
function ertesiGun(gun: string): string {
  const m = GUN_RE.exec(gun);
  if (!m) return gun;
  const t = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + 1));
  return `${String(t.getUTCFullYear()).padStart(4, "0")}-${String(t.getUTCMonth() + 1).padStart(2, "0")}-${String(t.getUTCDate()).padStart(2, "0")}`;
}

/**
 * Mesajdaki tarih — sunucuyla AYNI biçimleyici (tr-TR, Europe/Istanbul) ve AYNI an
 * (`new Date("YYYY-MM-DD")` = sunucuya giden UTC gece yarısı). Böylece "0026" yılı da
 * sunucudaki gibi görünür. Tarayıcı ayrıştıramazsa metinden gg.aa.yyyy kurulur.
 */
function trTarih(gun: string): string {
  const d = new Date(gun);
  if (!Number.isNaN(d.getTime())) return trTarihFmt.format(d);
  const m = GUN_RE.exec(gun);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : gun;
}

/** Yıl yazım hatası (2000–2100 dışı) — sunucudaki metinle aynı. Boş/geçerli ise null. */
function yilHatasi(ad: "Üretim tarihi" | "SKT", gun: string | null | undefined): string | null {
  if (!gun) return null;
  const m = GUN_RE.exec(gun);
  // `type="date"` yalnız boş ya da geçerli metin üretir; buraya düşmek beklenmez. Yine de
  // geçersiz metin sunucuya gidemez (`toISOString` patlar) — sessizce geçirmek yerine engelle.
  if (!m) return `${ad} geçersiz (${gun}) — tarihi gg.aa.yyyy biçiminde girin.`;
  const yil = Number(m[1]);
  if (yil < 2000 || yil > 2100) return `${ad} yılı hatalı (${trTarih(gun)}) — yılı 4 haneli girin (ör. 2026).`;
  return null;
}

/**
 * Alan bazında SKT kuralı. Üretim tarihinin yılı hatalıysa o alana yazılır ve SKT'nin üretim
 * tarihiyle karşılaştırması yapılmaz (hatalı tarihle kıyas anlamsız).
 */
export function sktAlanHatalari(g: SktKuralGirdi): SktAlanHatalari {
  const uretimHata = yilHatasi("Üretim tarihi", g.producedAt);
  let sktHata = yilHatasi("SKT", g.expiresAt);
  const skt = g.expiresAt;
  if (!sktHata && skt) {
    if (g.producedAt && !uretimHata && skt < g.producedAt) {
      sktHata = `SKT (${trTarih(skt)}) üretim tarihinden (${trTarih(g.producedAt)}) önce olamaz — tarihleri kontrol edin.`;
    } else if ((g.origin ?? "purchase") === "purchase" && g.kabulGunu && skt <= g.kabulGunu) {
      sktHata = `SKT (${trTarih(skt)}) kabul gününden (${trTarih(g.kabulGunu)}) sonra olmalı — SKT'si geçmiş ya da kabul günü dolan mal satın alma kabulüne girilemez. Tarihi kontrol edin.`;
    }
  }
  return { producedAt: uretimHata, expiresAt: sktHata };
}

/**
 * Sunucudaki `sktTarihHatasi` ile aynı mantık: İLK hata metni ya da null.
 * Sıra sunucuyla aynı: üretim yılı → SKT yılı → üretim tarihi kıyası → kabul günü kıyası.
 */
export function sktTarihHatasi(g: SktKuralGirdi): string | null {
  const h = sktAlanHatalari(g);
  return h.producedAt ?? h.expiresAt;
}

/**
 * SKT alanının `min`i: girilen (geçerli) üretim tarihi ile — satın almada — kabul gününün ERTESİ
 * gününün GEÇ olanı; hiçbiri yoksa yıl alt sınırı. Takvim bu günden öncesini seçtirmez; elle
 * yazılan değer yine `sktAlanHatalari` ile yakalanır.
 */
export function sktEnErkenGun(g: Omit<SktKuralGirdi, "expiresAt">): string {
  let min = SKT_YIL_MIN_GUN;
  if (g.producedAt && !yilHatasi("Üretim tarihi", g.producedAt) && g.producedAt > min) min = g.producedAt;
  if ((g.origin ?? "purchase") === "purchase" && g.kabulGunu) {
    const ertesi = ertesiGun(g.kabulGunu);
    if (ertesi > min) min = ertesi;
  }
  return min;
}

/**
 * Fiş düzenlemede MEVCUT lotun kayıtlı hâli (receiptDetail'den, İstanbul günlerine çevrilmiş).
 * Sunucu `updateReceiptLines` mevcut lotu KENDİ kabul anı ve kaynağıyla denetler.
 */
export interface SktMevcutLot {
  /** Kayıtlı üretim günü, "YYYY-MM-DD" (şemada zorunlu alan). */
  producedAt: string;
  /** Kayıtlı SKT günü, "YYYY-MM-DD" ya da "" (SKT'siz lot). */
  expiresAt: string;
  /** Lotun kabul günü = `createdAt`in İstanbul günü. */
  kabulGunu: string;
  origin: SktOrigin;
  /**
   * Kayıtlı üretim tarihi BOŞ bırakılmış alanın varsayılanı mı (üretim anı ≈ kabul anı, < 60 sn)? Sunucuyla
   * aynı sezgi: tarih alanından gelen değer gün başıdır, varsayılan ise kabul anının kendisi (V5).
   */
  varsayilanUretim: boolean;
}

/**
 * FİŞ DÜZENLEME bağlamı — `updateReceiptLines`in satır başına kararının birebir aynası:
 *  · Mevcut lot (`mevcut` dolu): kaynak + kabul günü LOTUN KENDİSİNDEN.
 *    Üretim günü = girilen ya da kayıtlı; değişti mi = kayıtlıdan farklı mı.
 *    SKT değişti mi = girilen SKT kayıtlıdan farklı mı.
 *    "Üretim tarihi girilmiş" = değişen yeni değer; değişmediyse kayıtlı değer YALNIZ satın almada ya da
 *    varsayılan (kabul anının kendisi) DEĞİLSE — açılış stoğunda boş bırakılan üretim tarihi kabul anıyla
 *    dolduğu için girilmiş sayılmaz.
 *    DENETİM: üretim ya da SKT değiştiyse — YA DA satır yerinde güncellenemiyorsa (ürün/lokasyon değişti
 *    → sunucu lotu yeniden açar ve `receiveGoodsTx` son savunması aynı bağlamla HER DURUMDA denetler).
 *    Değişmeyen satır denetlenmez (G6): yalnız miktarı düzeltilen eski kayıt kilitlenmesin.
 *  · Yeni satır (`mevcut` null): FİŞİN kaynağı + bugün; her zaman denetlenir.
 *
 * BOŞ SKT: üretim değişmediyse sunucu KAYITLI SKT'yi korur (V7) — istemci de onunla karar verir. Üretim
 * değiştiyse sunucu raf ömründen hesaplar; istemci bilemez → SKT kıyası yapılmaz (yıl kuralı yine işler).
 * İstemci yalnız gevşek kalabilir, sunucu mesajı bantta görünür.
 */
export function sktDuzenlemeKurali(
  form: { producedAt: string; expiresAt: string },
  mevcut: SktMevcutLot | null,
  baglam: { fisOrigin: SktOrigin; bugun: string; yerinde: boolean },
): { girdi: SktKuralGirdi; denetle: boolean } {
  if (!mevcut) {
    return {
      girdi: { expiresAt: form.expiresAt, producedAt: form.producedAt, kabulGunu: baglam.bugun, origin: baglam.fisOrigin },
      denetle: true,
    };
  }
  const uretim = form.producedAt || mevcut.producedAt || baglam.bugun;
  const uretimDegisti = uretim !== mevcut.producedAt;
  // SKT boşsa ve üretim DEĞİŞMEDİYSE sunucu KAYITLI SKT'yi korur (V7) — istemci onu bilir, aynı değerle karar verir.
  // Üretim değiştiyse sunucu raf ömründen hesaplar; istemci bilemez → boş kabul edilir (yalnız yıl kuralı).
  const sktEtkin = form.expiresAt || (uretimDegisti ? "" : mevcut.expiresAt);
  const sktDegisti = sktEtkin !== mevcut.expiresAt;
  const uretimGirilmis = form.producedAt && uretimDegisti
    ? form.producedAt
    : mevcut.origin === "purchase" || !mevcut.varsayilanUretim ? mevcut.producedAt : "";
  return {
    girdi: { expiresAt: sktEtkin, producedAt: uretimGirilmis, kabulGunu: mevcut.kabulGunu, origin: mevcut.origin },
    denetle: uretimDegisti || sktDegisti || !baglam.yerinde,
  };
}
